import { Transform, Type } from 'class-transformer';

import { ApiProperty, PartialType, PickType } from '../../codegen';
import { LangDto } from '../../dtos/lang.dto';
import {
  IsString,
  IsNotEmpty,
  CustomValidatePassword,
  IsEqualTo,
  IsBoolean,
  IsNumber,
  IsOptional,
  IsValidDateOfBirth,
  Min,
  ValidateNested,
} from '../../validators';

import { BasicUser } from './basic.user.entity';

// Safe DTO for MFA-related settings exposed publicly
export class UserSettingsPublicDto {
  @ApiProperty()
  @IsOptional()
  @IsBoolean()
  mfaEnabled?: boolean;

  @ApiProperty()
  @IsOptional()
  @IsString()
  mfaMethod?: string;
}

export class UserReturnDto extends PickType(BasicUser, [
  'id',
  'email',
  'name',
  'avatar',
  'dateOfBirth',
  'regData',
  'flags',
  'oauth',
  'gameStats',
  'lang',
]) {
  @ApiProperty()
  @IsNumber()
  @Min(1)
  minimumAge: number;

  @ApiProperty()
  @IsBoolean()
  isUnderMinimumAge: boolean;

  @ApiProperty()
  @Transform(({ value }) => {
    // value corresponds to BasicUser.settings JSONB
    if (value == null || typeof value !== 'object') return undefined;
    const v = value as Record<string, unknown>;
    const safe: Partial<UserSettingsPublicDto> = {};
    if (typeof v.mfaEnabled === 'boolean') safe.mfaEnabled = v.mfaEnabled;
    if (typeof v.mfaMethod === 'string') safe.mfaMethod = v.mfaMethod;
    // Deliberately exclude encryptedTotpSecret and any other secrets
    return Object.keys(safe).length > 0
      ? (safe as UserSettingsPublicDto)
      : undefined;
  })
  @IsOptional()
  @ValidateNested()
  @Type(() => UserSettingsPublicDto)
  settings?: UserSettingsPublicDto;
}

export class UserUpdateDto extends PartialType(
  PickType(BasicUser, ['name', 'avatar', 'flags', 'regData']),
) {
  @ApiProperty()
  @IsOptional()
  @IsString()
  @IsValidDateOfBirth()
  dateOfBirth?: string;

  @ApiProperty()
  @IsOptional()
  @Type(() => LangDto)
  @ValidateNested()
  lang?: LangDto;
}

export class UserUpdatePasswordDto {
  @ApiProperty()
  @IsString()
  oldPassword!: string;

  @ApiProperty()
  @IsString()
  @CustomValidatePassword()
  newPassword!: string;

  @ApiProperty()
  @IsString()
  @IsEqualTo('newPassword')
  confirmPassword!: string;

  // Index signature for decorators
  [key: string]: unknown;
}

export class UserDeleteDto {
  @ApiProperty()
  @IsNotEmpty()
  password!: string;
}
